import type { GeneratedDocument } from "./documents";
import { authenticatedBlobFetch, authenticatedFetch } from "./http";

export interface DeliveryDestination {
  id: string;
  clientId: string;
  clientCode: string;
  code: string;
  displayName: string;
  type: "POSTAL_ADDRESS" | "POU";
  processPointId: string | null;
  processPointCode: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  postalCode: string | null;
  city: string | null;
  region: string | null;
  countryCode: string | null;
  contactName: string | null;
  contactPhone: string | null;
  siteName: string | null;
  building: string | null;
  assemblyLine: string | null;
  workArea: string | null;
  scanCode: string | null;
  instructions: string | null;
  status: "ACTIVE" | "INACTIVE";
  version: number;
}

export interface DeliveryClient {
  id: string;
  organizationId: string;
  code: string;
  displayName: string;
  status: "ACTIVE" | "INACTIVE";
  version: number;
  destinations: DeliveryDestination[];
}

export function loadDeliveryClients(): Promise<DeliveryClient[]> {
  return authenticatedFetch<DeliveryClient[]>(
    "/api/v1/delivery-master-data/clients"
  );
}

export function createDeliveryClient(
  request: Record<string, unknown>
): Promise<DeliveryClient> {
  return authenticatedFetch<DeliveryClient>(
    "/api/v1/delivery-master-data/clients",
    { method: "POST", body: JSON.stringify(request) }
  );
}

export function updateDeliveryClient(
  id: string, request: Record<string, unknown>
): Promise<DeliveryClient> {
  return authenticatedFetch<DeliveryClient>(
    `/api/v1/delivery-master-data/clients/${id}`,
    { method: "PATCH", body: JSON.stringify(request) }
  );
}

export function archiveDeliveryClient(
  id: string, version: number, reason: string
): Promise<DeliveryClient> {
  return authenticatedFetch<DeliveryClient>(
    `/api/v1/delivery-master-data/clients/${id}`,
    { method: "DELETE", body: JSON.stringify({ version, reason }) }
  );
}

export function createDeliveryDestination(
  request: Record<string, unknown>
): Promise<DeliveryDestination> {
  return authenticatedFetch<DeliveryDestination>(
    "/api/v1/delivery-master-data/destinations",
    { method: "POST", body: JSON.stringify(request) }
  );
}

export function updateDeliveryDestination(
  id: string, request: Record<string, unknown>
): Promise<DeliveryDestination> {
  return authenticatedFetch<DeliveryDestination>(
    `/api/v1/delivery-master-data/destinations/${id}`,
    { method: "PATCH", body: JSON.stringify(request) }
  );
}

export function archiveDeliveryDestination(
  id: string, version: number, reason: string
): Promise<DeliveryDestination> {
  return authenticatedFetch<DeliveryDestination>(
    `/api/v1/delivery-master-data/destinations/${id}`,
    { method: "DELETE", body: JSON.stringify({ version, reason }) }
  );
}

export interface EligibleDeliveryPreparation {
  taskId: string;
  taskNumber: string;
  orderId: string;
  orderNumber: string;
  outboundOrderId: string | null;
  outboundOrderNumber: string | null;
  outboundExternalReference: string | null;
  clientId: string | null;
  clientCode: string | null;
  deliveryDestinationId: string | null;
  deliveryDestinationCode: string | null;
  deliveryDestinationName: string | null;
  warehouseId: string;
  warehouseCode: string;
  sourceProcessPointId: string;
  sourceProcessPointCode: string;
  handlingUnitId: string;
  handlingUnitNumber: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  uom: string;
}

export interface DeliveryMissionLine {
  preparationTaskId: string;
  preparationTaskNumber: string;
  outboundOrderId: string | null;
  outboundOrderNumber: string | null;
  handlingUnitId: string;
  handlingUnitNumber: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  uom: string;
  sequenceNumber: number;
}

export interface AvailableDeliveryHandlingEquipment {
  id: string;
  warehouseId: string;
  typeCode: string;
  typeName: string;
  maxLoadKg: number;
  identifier: string;
  status: string;
  currentProcessPointCode: string | null;
  version: number;
}

export interface DeliveryMission {
  id: string;
  missionNumber: string;
  consolidationId: string;
  consolidationNumber: string;
  consolidationMode: "ORIGINAL_HU" | "TROLLEY" | "REPACK";
  trolleyNumber: string | null;
  repackedHandlingUnitNumber: string | null;
  clientId: string;
  clientCode: string;
  warehouseId: string;
  warehouseCode: string;
  destinationId: string;
  destinationCode: string;
  destinationName: string;
  destinationType: "POSTAL_ADDRESS" | "POU";
  destinationScanCode: string | null;
  status: "OPEN" | "IN_PROGRESS" | "COMPLETED" | "EXCEPTION" | "CANCELLED";
  priority: number;
  assignedUserName: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  version: number;
  lines: DeliveryMissionLine[];
}

export function loadEligibleDeliveryPreparations():
Promise<EligibleDeliveryPreparation[]> {
  return authenticatedFetch<EligibleDeliveryPreparation[]>(
    "/api/v1/deliveries/eligible-preparations"
  );
}

export function loadDeliveryMissions(): Promise<DeliveryMission[]> {
  return authenticatedFetch<DeliveryMission[]>("/api/v1/deliveries/missions");
}

export function loadAvailableDeliveryHandlingEquipment(
  warehouseId: string
): Promise<AvailableDeliveryHandlingEquipment[]> {
  return authenticatedFetch<AvailableDeliveryHandlingEquipment[]>(
    `/api/v1/deliveries/available-handling-equipment?warehouseId=${warehouseId}`
  );
}

export function loadGeneratedDeliveryDocuments(
  missionId: string
): Promise<GeneratedDocument[]> {
  return authenticatedFetch<GeneratedDocument[]>(
    `/api/v1/generated-documents/delivery-missions/${missionId}`
  );
}

export function loadGeneratedDeliveryDocumentContent(
  missionId: string, documentId: string
): Promise<Blob> {
  return authenticatedBlobFetch(
    `/api/v1/generated-documents/delivery-missions/${missionId}/${documentId}/content`
  );
}

export function loadGeneratedPutawayDocumentContent(
  taskId: string, documentId: string
): Promise<Blob> {
  return authenticatedBlobFetch(
    `/api/v1/generated-documents/putaway-tasks/${taskId}/${documentId}/content`
  );
}

export function createTrolleyDeliveryMission(
  preparationTaskIds: string[], destinationId: string,
  handlingEquipmentId: string | null, priority: number,
  handlingEquipmentIdentifier: string | null = null
): Promise<DeliveryMission> {
  return authenticatedFetch<DeliveryMission>(
    "/api/v1/deliveries/trolley-missions",
    {
      method: "POST",
      body: JSON.stringify({
        preparationTaskIds, destinationId, handlingEquipmentId,
        handlingEquipmentIdentifier, priority
      })
    }
  );
}

export function createDirectDeliveryMission(
  preparationTaskIds: string[], destinationId: string, priority: number
): Promise<DeliveryMission> {
  return authenticatedFetch<DeliveryMission>(
    "/api/v1/deliveries/direct-missions",
    {
      method: "POST",
      body: JSON.stringify({ preparationTaskIds, destinationId, priority })
    }
  );
}

export interface OutboundRepackagingOption {
  id: string;
  code: string;
  displayName: string;
  uom: string;
}

export interface OutboundRepackaging {
  id: string;
  warehouseId: string;
  consolidationNumber: string;
  status:
    | "WAITING_TRANSFER"
    | "OPEN"
    | "IN_PROGRESS"
    | "COMPLETED"
    | "CANCELLED";
  priority: number;
  clientCode: string;
  destinationCode: string;
  repackingProcessPointCode: string;
  targetPackagingCode: string;
  outputHandlingUnitNumber: string | null;
  assignedUserName: string | null;
  createdAt: string;
  version: number;
  sourceHandlingUnitNumbers: string[];
}

export function loadOutboundRepackagings(): Promise<OutboundRepackaging[]> {
  return authenticatedFetch<OutboundRepackaging[]>(
    "/api/v1/deliveries/outbound-repackagings"
  );
}

export function loadOutboundRepackagingOptions(
  preparationTaskId: string
): Promise<OutboundRepackagingOption[]> {
  return authenticatedFetch<OutboundRepackagingOption[]>(
    `/api/v1/deliveries/outbound-repackaging-options/${preparationTaskId}`
  );
}

export function createOutboundRepackaging(
  preparationTaskIds: string[], destinationId: string,
  repackingProcessPointId: string, targetItemPackagingId: string,
  priority: number
): Promise<OutboundRepackaging> {
  return authenticatedFetch<OutboundRepackaging>(
    "/api/v1/deliveries/outbound-repackagings",
    {
      method: "POST",
      body: JSON.stringify({
        preparationTaskIds, destinationId, repackingProcessPointId,
        targetItemPackagingId, priority
      })
    }
  );
}

export function takeOutboundRepackaging(
  id: string, version: number
): Promise<OutboundRepackaging> {
  return authenticatedFetch<OutboundRepackaging>(
    `/api/v1/deliveries/outbound-repackagings/${id}/take`,
    { method: "POST", body: JSON.stringify({ version }) }
  );
}

export function completeOutboundRepackaging(
  id: string, version: number, handlingUnitNumbers: string[]
): Promise<DeliveryMission> {
  return authenticatedFetch<DeliveryMission>(
    `/api/v1/deliveries/outbound-repackagings/${id}/complete`,
    {
      method: "POST",
      body: JSON.stringify({ version, handlingUnitNumbers })
    }
  );
}

export function takeDeliveryMission(
  id: string, version: number
): Promise<DeliveryMission> {
  return authenticatedFetch<DeliveryMission>(
    `/api/v1/deliveries/missions/${id}/take`,
    { method: "POST", body: JSON.stringify({ version }) }
  );
}

export function completeDeliveryMission(
  id: string, trolleyNumber: string,
  destinationScanCode: string, version: number
): Promise<DeliveryMission> {
  return authenticatedFetch<DeliveryMission>(
    `/api/v1/deliveries/missions/${id}/complete`,
    {
      method: "POST",
      body: JSON.stringify({ trolleyNumber, destinationScanCode, version })
    }
  );
}

export function completeDirectDeliveryMission(
  id: string, handlingUnitNumbers: string[],
  destinationScanCode: string | null, version: number
): Promise<DeliveryMission> {
  return authenticatedFetch<DeliveryMission>(
    `/api/v1/deliveries/missions/${id}/complete-direct`,
    {
      method: "POST",
      body: JSON.stringify({
        handlingUnitNumbers, destinationScanCode, version
      })
    }
  );
}
